import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { KTSVG, LoadingGrid } from '../../../../../rbts/helpers';
import { useObtenerEventosBoletera } from '../hooks/EventosBoleteraHook';
import { ModalNuevoEvento } from '../_modals/ModalNuevoEvento';
import { EventoBoleteraModel } from '../models/EventoBoleteraModel';
import { cambiarEstadoEvento } from '../../../../service/boletera/EventosBoletera';

const STATUS_BADGE: Record<string, string> = {
  ACTIVE: 'badge-light-success',
  PEND:   'badge-light-warning',
  PAUSE:  'badge-light-secondary',
  FINISH: 'badge-light-danger',
};

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Publicado',
  PEND:   'Borrador',
  PAUSE:  'Pausado',
  FINISH: 'Finalizado',
};

const List: React.FC = () => {
  const [reload, setReload]       = useState(0);
  const { list, loading }         = useObtenerEventosBoletera(reload);
  const [showModal, setShowModal] = useState(false);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const history = useHistory();

  const handleEstado = async (ev: EventoBoleteraModel, accion: 'publicar' | 'pausar' | 'despublicar') => {
    if (!ev.id) return;
    setLoadingId(ev.id);
    try {
      await cambiarEstadoEvento(ev.id, accion);
      setReload((r) => r + 1);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <>
      <div className="card card-xxl-stretch mb-xl-12">
        <div className="card-header border-0 pt-5 mt-4 d-flex">
          <div>
            <h3 className="card-title fw-bolder">Eventos — Boletera</h3>
            <div className="text-muted fs-7">Creación de evento, niveles de entrada y aforo.</div>
          </div>
          <div className="text-end">
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <KTSVG path="/media/icons/duotune/arrows/arr075.svg" className="svg-icon-2" />
              + Nuevo evento
            </button>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <LoadingGrid text="Cargando..." />
          ) : (
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                <thead>
                  <tr className="fw-bolder text-muted">
                    <th>EVENTO</th>
                    <th>RECINTO</th>
                    <th>FECHA</th>
                    <th>ESTADO</th>
                    <th>VENDIDOS / AFORO</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-10">
                        No hay eventos. Crea el primero con el botón "+ Nuevo evento".
                      </td>
                    </tr>
                  ) : (
                    list.map((ev: EventoBoleteraModel) => {
                      const statusCode = ev.status?.code || 'PEND';
                      const vendidos   = ev.vendidos_total ?? 0;
                      const aforo      = ev.aforo_max_evento || 0;
                      const pct        = aforo > 0 ? Math.min(100, Math.round((vendidos / aforo) * 100)) : 0;
                      const fecha      = ev.fecha_inicio
                        ? new Date(ev.fecha_inicio).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—';
                      const busy = loadingId === ev.id;
                      return (
                        <tr key={ev.id}>
                          <td>
                            <div className="fw-bold">{ev.nombre}</div>
                            {ev.tipo && <div className="text-muted fs-7">{ev.tipo}</div>}
                          </td>
                          <td>{ev.recinto?.nombre || '—'}</td>
                          <td>{fecha}</td>
                          <td>
                            <span className={`badge ${STATUS_BADGE[statusCode] || 'badge-light'}`}>
                              {STATUS_LABEL[statusCode] || statusCode}
                            </span>
                          </td>
                          <td style={{ minWidth: 140 }}>
                            <div className="d-flex align-items-center gap-2">
                              <div className="progress flex-grow-1 h-6px" style={{ minWidth: 60 }}>
                                <div className="progress-bar bg-warning" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-muted fs-7 text-nowrap">
                                {vendidos.toLocaleString()} / {aforo.toLocaleString()}
                              </span>
                            </div>
                          </td>
                          <td className="text-end">
                            <div className="d-flex gap-2 justify-content-end flex-wrap">
                              {statusCode === 'PEND' && (
                                <button
                                  className="btn btn-sm btn-light-success"
                                  disabled={busy}
                                  onClick={() => handleEstado(ev, 'publicar')}
                                >
                                  Publicar
                                </button>
                              )}
                              {statusCode === 'ACTIVE' && (
                                <button
                                  className="btn btn-sm btn-light-warning"
                                  disabled={busy}
                                  onClick={() => handleEstado(ev, 'pausar')}
                                >
                                  Pausar
                                </button>
                              )}
                              {statusCode === 'PAUSE' && (
                                <button
                                  className="btn btn-sm btn-light-success"
                                  disabled={busy}
                                  onClick={() => handleEstado(ev, 'publicar')}
                                >
                                  Reanudar
                                </button>
                              )}
                              {(statusCode === 'ACTIVE' || statusCode === 'PAUSE') && (
                                <button
                                  className="btn btn-sm btn-light-secondary"
                                  disabled={busy}
                                  onClick={() => handleEstado(ev, 'despublicar')}
                                >
                                  Despublicar
                                </button>
                              )}
                              <button
                                className="btn btn-sm btn-light-primary"
                                onClick={() => history.push(`/boletera/eventos/${ev.id}`)}
                              >
                                Ver / editar
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ModalNuevoEvento
        show={showModal}
        handleClose={(refresh) => {
          setShowModal(false);
          if (refresh) setReload((r) => r + 1);
        }}
      />
    </>
  );
};

export { List };
